import React, { memo, useCallback, useMemo } from "react";
import {
  Button,
  Center,
  Divider,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  HStack,
  Spacer,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import colors from "app/colors";
import AdminEditUserAttributeCategoryName from "./admin-user-attribute-category/admin-edit-user-attribute-category-name";
import { AccountBoxIcon, CirclePlusIcon } from "modules/icons";
import AdminEditUserAttribute from "./admin-user-attribute-category/admin-edit-user-attribute";
import { useOverlay } from "modules/providers/overlay-provider";
import { MODAL_TYPE } from "modules/providers/overlay-provider/overlay-provider";
import { useUserAttributes } from "modules/providers/user-attributes-provider";
import { useTags } from "modules/providers/tags-provider";
import { useToggle } from "react-use";
import SyncoTooltip from "modules/_shared/synco-tooltip";
import { sortByName } from "modules/utils/Functions";

interface IProps {
  userAttributeCategoryId: string;
}

const AdminUserAttributeCategoryDrawer = ({
  userAttributeCategoryId,
}: IProps) => {
  const { openModal } = useOverlay();
  const {
    userAttributeCategoriesById,
    userAttributesByUserAttributeCategoryId,
  } = useUserAttributes();
  const { tagCategoriesById } = useTags();

  const [isDisplayInactive, toggleDisplayInactive] = useToggle(false);

  const userAttributeCategory = useMemo(
    () => userAttributeCategoriesById[userAttributeCategoryId],
    [userAttributeCategoryId, userAttributeCategoriesById],
  );

  const userAttributes = useMemo(
    () =>
      userAttributesByUserAttributeCategoryId[userAttributeCategoryId] || [],
    [userAttributeCategoryId, userAttributesByUserAttributeCategoryId],
  );

  const activeUserAttributes = useMemo(
    () =>
      [...userAttributes]
        .filter((userAttribute) => userAttribute.active)
        .sort(sortByName),
    [userAttributes],
  );

  const inactiveUserAttributes = useMemo(
    () =>
      [...userAttributes]
        .filter((userAttribute) => !userAttribute.active)
        .sort(sortByName),
    [userAttributes],
  );

  const tagCategory = useMemo(
    () =>
      userAttributeCategory?.tagCategoryId
        ? tagCategoriesById[userAttributeCategory.tagCategoryId]
        : undefined,
    [tagCategoriesById, userAttributeCategory],
  );

  const onAddAttributeClicked = useCallback(() => {
    openModal({
      type: MODAL_TYPE.ADMIN_CREATE_USER_ATTRIBUTE_TAG,
      id: userAttributeCategoryId,
    });
  }, [openModal, userAttributeCategoryId]);

  if (!userAttributeCategory) {
    return null;
  }

  //tagCategory.type === TagCategoryType.UserTag should be read-only
  if (tagCategory) {
    return (
      <DrawerContent>
        <DrawerCloseButton left={6} top={8} color={colors.gray30} size="lg" />
        <DrawerHeader pt={8}>
          <Center fontWeight={700} fontFamily="Outfit" fontSize={26}>
            User Attribute Category Details
          </Center>
        </DrawerHeader>
        <DrawerBody p={0}>
          <Stack spacing={6} px={8}>
            <VStack spacing={1} w="full" alignItems="flex-start">
              <AdminEditUserAttributeCategoryName
                userAttributeCategory={userAttributeCategory}
              />
              <Text
                fontSize={14}
                fontWeight={500}
                color={colors.blackSecondary}
              >
                Linked to tag category: {tagCategory.name}
              </Text>
            </VStack>
            <VStack spacing={3} w="full" alignItems="flex-start">
              <HStack
                w="full"
                borderBottomWidth={1}
                borderBottomColor={colors.black}
              >
                <AccountBoxIcon boxSize={6} />
                <Text fontWeight={600} fontSize={18}>
                  {`Attributes (${userAttributes.length})`}
                </Text>
                <Spacer />
                <SyncoTooltip label="This tag is used as a user attribute">
                  <HStack
                    color={colors.orangePrimary}
                    fontSize={12}
                    fontWeight={500}
                    spacing={1}
                    opacity={0.5}
                    cursor="not-allowed"
                  >
                    <CirclePlusIcon boxSize={4} fill={colors.orangePrimary} />
                    <Text>Add Attribute</Text>
                  </HStack>
                </SyncoTooltip>
              </HStack>
              <VStack w="full" alignItems="flex-start" divider={<Divider />}>
                {activeUserAttributes.map((userAttribute) => (
                  <Text
                    key={userAttribute.id}
                    fontSize={14}
                    fontWeight={600}
                    color={colors.gray50}
                    w="full"
                  >
                    {userAttribute.name}
                  </Text>
                ))}
              </VStack>
            </VStack>
            {inactiveUserAttributes.length && (
              <VStack spacing={3} w="full" alignItems="flex-start">
                <Button
                  variant="link"
                  color={colors.blue}
                  fontWeight={500}
                  fontSize={12}
                  onClick={toggleDisplayInactive}
                >
                  {isDisplayInactive
                    ? `+ Show Deactivated Attributes (${inactiveUserAttributes.length})`
                    : `- Hide Deactivated Attributes (${inactiveUserAttributes.length})`}
                </Button>
                {!isDisplayInactive && (
                  <VStack
                    w="full"
                    alignItems="flex-start"
                    divider={<Divider />}
                  >
                    {inactiveUserAttributes.map((userAttribute) => (
                      <Text
                        key={userAttribute.id}
                        fontSize={14}
                        fontWeight={500}
                        color={colors.grayMain}
                        fontStyle="italic"
                        w="full"
                      >
                        {userAttribute.name}
                      </Text>
                    ))}
                  </VStack>
                )}
              </VStack>
            )}
          </Stack>
        </DrawerBody>
      </DrawerContent>
    );
  }

  return (
    <DrawerContent>
      <DrawerCloseButton left={6} top={8} color={colors.gray30} size="lg" />
      <DrawerHeader pt={8}>
        <Center fontWeight={700} fontFamily="Outfit" fontSize={26}>
          User Attribute Category Details
        </Center>
      </DrawerHeader>
      <DrawerBody p={0}>
        <Stack spacing={6} px={8}>
          <AdminEditUserAttributeCategoryName
            userAttributeCategory={userAttributeCategory}
          />
          <VStack spacing={3} w="full" alignItems="flex-start">
            <HStack
              w="full"
              borderBottomWidth={1}
              borderBottomColor={colors.black}
            >
              <AccountBoxIcon boxSize={6} />
              <Text fontWeight={600} fontSize={18}>
                {`Attributes (${userAttributes.length})`}
              </Text>
              <Spacer />
              <Button
                variant="ghost"
                color={colors.orangePrimary}
                fontSize={12}
                fontWeight={500}
                size="xs"
                leftIcon={
                  <CirclePlusIcon boxSize={4} fill={colors.orangePrimary} />
                }
                onClick={onAddAttributeClicked}
              >
                Add Attribute
              </Button>
            </HStack>
            <VStack w="full" alignItems="flex-start" divider={<Divider />}>
              {activeUserAttributes.map((userAttribute) => (
                <AdminEditUserAttribute
                  userAttribute={userAttribute}
                  key={userAttribute.id}
                />
              ))}
            </VStack>
          </VStack>
          {inactiveUserAttributes.length && (
            <VStack spacing={3} w="full" alignItems="flex-start">
              <Button
                variant="link"
                color={colors.blue}
                fontWeight={500}
                fontSize={12}
                onClick={toggleDisplayInactive}
              >
                {isDisplayInactive
                  ? `+ Show Deactivated Attributes (${inactiveUserAttributes.length})`
                  : `- Hide Deactivated Attributes (${inactiveUserAttributes.length})`}
              </Button>
              {!isDisplayInactive && (
                <VStack w="full" alignItems="flex-start" divider={<Divider />}>
                  {inactiveUserAttributes.map((userAttribute) => (
                    <AdminEditUserAttribute
                      userAttribute={userAttribute}
                      key={userAttribute.id}
                    />
                  ))}
                </VStack>
              )}
            </VStack>
          )}
        </Stack>
      </DrawerBody>
    </DrawerContent>
  );
};

export default memo(AdminUserAttributeCategoryDrawer);
